import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { numOrZero } from '@core/utils';
import { DestroyDirective } from '../destroy-abstract/destroy.abstract';
import { WizardService } from '@core/services/wizard/wizard.service';
import { takeUntil } from 'rxjs/operators';
import { WizardStepDTO } from '@core/modules/api/models';
import { AppService } from '@core/services/app/app.service';

@Component({
  selector: 'app-relative-stepper',
  templateUrl: './relative-stepper.component.html',
  styleUrls: ['./relative-stepper.component.scss']
})
export class RelativeStepperComponent
  extends DestroyDirective
  implements OnInit, OnChanges
{
  /**
   * numero di step
   */
  @Input() totalSteps: number;

  /**
   * index segmento attivo
   */
  @Input() activeIndex = 0;

  /**
   * index segmento attivo effettivo
   */
  effectiveActiveIndex = 0;

  /**
   * lista di segments derivati dal numero di step impostati
   */
  segments: number[] = [];

  constructor(
    private wizardService: WizardService,
    private appService: AppService
  ) {
    super();
  }

  ngOnInit() {
    this.watchWizard();
    this.updateStepper();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeIndex || changes.totalSteps) {
      this.updateStepper();
    }
  }

  /**
   * osserva i cambiamenti del wizard
   */
  private watchWizard(): void {
    this.wizardService
      .getWizardWhenChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateStepper();
      });
  }

  /**
   * aggiorna lo stepper (segmenti e index)
   */
  private updateStepper(): void {
    this.updateSegments();
    this.updateEffectiveActiveIndex();
  }

  /**
   * aggiorna l'array di segments
   */
  private updateSegments(): void {
    const totalLength =
      numOrZero(this.totalSteps) + this.getWizardSameGroupSegmentsIncrement();

    this.segments = Array.from(Array(totalLength).keys());
  }

  /**
   * aggiorna l'index effetivo
   */
  private updateEffectiveActiveIndex(): void {
    this.effectiveActiveIndex =
      numOrZero(this.activeIndex) +
      this.getWizardSameGroupActiveIndexIncrement();
  }

  /**
   * restituisce l'incremento previsto per i segmenti in base al raggruppamento degli step del wizard
   */
  private getWizardSameGroupSegmentsIncrement(): number {
    const length = numOrZero(this.getWizardSameGroupSteps().length);

    if (length > 0) {
      return length - 1;
    } else {
      return 0;
    }
  }

  /**
   * restituisce l'incremento previsto per l'active index in base al raggruppamento degli step del wizard
   */
  private getWizardSameGroupActiveIndexIncrement(): number {
    const stepIndex = numOrZero(this.getWizardStepIndex());

    if (stepIndex >= 0) {
      return stepIndex;
    } else {
      return 0;
    }
  }

  /**
   * restituisce l'index dello step del wizard sulla base degli step che appartengono allo stesso gruppo
   */
  private getWizardStepIndex(): number {
    const activeStep = this.wizardService.getActiveStep();

    return this.getWizardSameGroupSteps().findIndex(
      step => activeStep && activeStep.stepCode === step.stepCode
    );
  }

  /**
   * restituisce la lista degli step che appartengono allo stesso gruppo
   */
  private getWizardSameGroupSteps(): WizardStepDTO[] {
    const groupCode = this.getGroupCode();

    return this.wizardService
      .getWizardSteps()
      .filter(step => !!groupCode && step.groupCod === groupCode);
  }

  /**
   * restituisce il group code previsto per questo step del wizard
   */
  private getGroupCode(): string {
    const activeStep = this.wizardService.getActiveStep();
    const progressBarGroups =
      this.appService.getConfig().wizard.progressBarGroups || [];

    if (
      !!activeStep &&
      !!activeStep.groupCod &&
      progressBarGroups.includes(activeStep.groupCod)
    ) {
      return activeStep.groupCod;
    }
  }
}
